import React from "react";
import { BasicModal } from "../BasicModal/BasicModal";
import BasicButton from "../../Buttons/BasicButton";
import styles from "./styles.module.scss";

export type PaymentResult = "success" | "failed";

type Props = {
  result: PaymentResult;
  isOpened: boolean;
  closeModal: () => void;
  // Success: leads to the balance page. Failed: reopens the top-up methods.
  onAction: () => void;
};

const COPY: Record<
  PaymentResult,
  { title: string; text: string; action: string }
> = {
  success: {
    title: "Payment received",
    text: "Thank you! Your balance is updated as soon as the payment system confirms the payment — usually within a few minutes.",
    action: "View balance",
  },
  failed: {
    title: "Payment failed",
    text: "The payment didn't go through and no funds were added to your balance. Try again or choose another top-up method.",
    action: "Try again",
  },
};

const SuccessIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M5 12.5l4.5 4.5L19 7.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FailedIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** What the buyer sees after the payment system sends them back to us. */
export const PaymentResultModal = ({
  result,
  isOpened,
  closeModal,
  onAction,
}: Props) => {
  const { title, text, action } = COPY[result];

  return (
    <BasicModal
      isOpened={isOpened}
      closeModal={closeModal}
      sx={{ width: "fit-content" }}
    >
      <div className={styles["inner"]} role="dialog" aria-label={title}>
        <span className={`${styles["icon"]} ${styles[result]}`}>
          {result === "success" ? <SuccessIcon /> : <FailedIcon />}
        </span>
        <h2 className={styles["title"]}>{title}</h2>
        <p className={styles["text"]}>{text}</p>
        <div className={styles["btn-box"]}>
          <BasicButton
            size="large"
            width="190px"
            variant="secondary"
            onClick={closeModal}
          >
            Close
          </BasicButton>
          <BasicButton
            size="large"
            width="190px"
            variant="primary"
            onClick={onAction}
          >
            {action}
          </BasicButton>
        </div>
      </div>
    </BasicModal>
  );
};
